export const READABLE_PROGRAM_NAMES = {
  PrimaryCare: 'Primary Care',
  UrgentCare: 'Urgent Care',
  ReturnToWork: 'Return To Work',
  ChronicOpioidUse: 'Chronic Opioid Use',
  ComplexPatient: 'Complex Patient',
  WeightLoss: 'Weight Management',
  AdhdAdd: 'A.D.H.D. / A.D.D.',
  DepressionAndAnxiety: 'Depression And Anxiety',
  Benefit: 'Health Plan',
}

// The primary-care subset of the API's shared enrollment-status enum (which also carries the
// benefit statuses). Extract keeps both guarantees: the build fails if the API renames or
// removes a value, and a benefit-only status can't sneak into PRIMARY_CARE_STATUS.
// Display labels for these statuses live in PRIMARY_CARE_STATUS_DISPLAY below; a status
// without one renders its raw backend value.
type PrimaryCareEnrollmentStatus = Extract<
  NonNullable<import('@fireflyhealth/api').ProgramsPeopleEnrollmentListResponse[number]['status']>,
  'Not Established' | 'Established' | 'Churned' | 'Unknown' | 'Episodic Care'
>

export const PRIMARY_CARE_STATUS = {
  NOT_ESTABLISHED: 'Not Established',
  EPISODIC_CARE: 'Episodic Care',
  UNKNOWN: 'Unknown',
  ESTABLISHED: 'Established',
  CHURNED: 'Churned',
} satisfies Record<string, PrimaryCareEnrollmentStatus>

// Pre-establishment statuses share the same projected journey: care-team selection moves the
// member to Not Established, then establishment and (potentially) churn.
export const PRE_ESTABLISHMENT_STATUSES: PrimaryCareEnrollmentStatus[] = [
  PRIMARY_CARE_STATUS.NOT_ESTABLISHED,
  PRIMARY_CARE_STATUS.EPISODIC_CARE,
  PRIMARY_CARE_STATUS.UNKNOWN,
]

// Provider-facing display labels (product rollout doc). Display names are product copy and live
// here in the display layer; the backend values stay brand-free. Unmapped statuses (Churned,
// Unknown) render their raw value. `satisfies` ties the keys to the API-derived status union, so
// a renamed or non-primary-care key fails the build instead of silently falling through.
const PRIMARY_CARE_STATUS_DISPLAY = {
  [PRIMARY_CARE_STATUS.EPISODIC_CARE]: 'Non-PCP',
  [PRIMARY_CARE_STATUS.NOT_ESTABLISHED]: 'PCP Visit Pending',
  [PRIMARY_CARE_STATUS.ESTABLISHED]: 'PCP Established',
} satisfies Partial<Record<PrimaryCareEnrollmentStatus, string>>

const hasDisplayLabel = (status: string): status is keyof typeof PRIMARY_CARE_STATUS_DISPLAY =>
  status in PRIMARY_CARE_STATUS_DISPLAY

export const getPrimaryCareStatusDisplay = (status: string): string =>
  hasDisplayLabel(status) ? PRIMARY_CARE_STATUS_DISPLAY[status] : status

export const BENEFIT_PROGRAM_STATUSES = {
  NotEnrolled: 'Not enrolled',
  Elected: 'Elected',
  Enrolled: 'Enrolled',
  Terminated: 'Terminated',
}

export const ProgramEnrollmentEvents = {
  CARE_ESTABLISHED: 'CARE_ESTABLISHED',
  PROGRAM_ENROLLED: 'PROGRAM_ENROLLED',
  PROGRAM_REENROLLED: 'PROGRAM_REENROLLED',
  PROGRAM_UNENROLLED: 'PROGRAM_UNENROLLED',
}

export const PRIMARY_CARE_PROGRAM_UID = 'PrimaryCare'
export const BENEFIT_PROGRAM_UID = 'Benefit'
export const DISCHARGE_IN_PROGRESS_TEXT = '(Discharge in progress)'

export const NO_ENROLLMENT_TEXT = {
  PrimaryCare: 'Member is not enrolled in Firefly primary care',
  Benefit: 'Member is not recognized as eligible for the Firefly health plan',
}

export const MISSING_DATA_INFO_TEXT =
  'Member must have all required demographic information present before enrollment'
